import {
  Button,
  CurrencyInput,
  DateTimePicker,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useRef } from "react";

interface LogPurchaseFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const LogPurchaseForm = ({ contactId, onSubmit, onCancel }: LogPurchaseFormProps) => {
  const { t } = useTranslation("payments");

  const productsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const logPurchaseMutation = useTellescopeMutation({
    model: "purchases",
    method: "createOne",

    onSuccess: () => {
      onSubmit?.();
    },
  });

  const initialPurchasedAtRef = useRef<Date | null>(null);
  if (initialPurchasedAtRef.current === null) {
    initialPurchasedAtRef.current = new Date();
  }

  const form = useForm({
    fields: {
      productId: {
        type: "select",
        initialValue: productsQuery.data?.length === 1 ? productsQuery.data[0]?.id ?? "" : "",
        required: true,
      },
      cost: {
        type: "number",
        initialValue: 0,
        required: true,
        dependencies: ["productId"],
      },
      purchasedAt: {
        type: "date",
        initialValue: initialPurchasedAtRef.current,
      },
      notes: {
        type: "textarea",
        initialValue: "",
      },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      logPurchaseMutation.mutate({
        title: t("Logged Purchase"),
        processor: "Stripe",
        enduserId: contactId,
        productId: values.productId,
        cost: {
          amount: values.cost,
          currency: "USD", // TODO: support other currencies
        },
        processedAt: values.purchasedAt,
        notes: values.notes,
      });
    },
    sideEffects: {
      productId: (value) => {
        const productCost = productsQuery.data?.find((product) => product.id === value)?.cost.amount;
        if (productCost === undefined) {
          return {};
        }

        return {
          cost: productCost,
        };
      },
    },
  });

  return (
    <form className="flex flex-col h-full overflow-y-auto p-2" {...form.getFormProps()}>
      <div className="flex-1 grow-1 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 p-1">
            <Label htmlFor="product">{t("Select Product")}</Label>
            <Select {...form.getCustomFieldProps("productId")}>
              <SelectTrigger id="product" className="w-full">
                <SelectValue placeholder={t("select Product")} />
              </SelectTrigger>
              <SelectContent>
                {productsQuery.data?.map(({ id, title }) => (
                  <SelectItem key={id} value={id}>
                    {title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 p-1">
            <Label htmlFor="cost" className={!form.values.productId ? "text-muted-foreground" : undefined}>
              {/* TODO: support other currencies */}
              {t("Cost in Dollars ($)")}
            </Label>
            <CurrencyInput {...form.getCustomFieldProps("cost")} disabled={!form.values.productId} />
          </div>
          <DateTimePicker
            {...form.getCustomFieldProps("purchasedAt")}
            id="processedAt"
            className="space-y-2 p-1"
            placeholder={t("Purchased At")}
          />
          <div className="space-y-2 p-1">
            <Label htmlFor="title">{t("Add Notes")}</Label>
            <Textarea {...form.getFieldProps("notes")} />
          </div>
        </div>
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!form.isValid} type="submit">
          {t("Save")}
        </Button>
      </div>
    </form>
  );
};
